// components/CustomToast.tsx
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import React, { useEffect } from 'react';

export type ToastType = 'success' | 'error';

interface CustomToastProps {
  message: string;
  onClose: () => void;
  duration?: number;
  className?: string;
  type?: ToastType;
}

const CustomToast: React.FC<CustomToastProps> = ({
  message,
  onClose,
  duration = 3000,
  className = '',
  type = 'success',
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [onClose, duration]);

  const isSuccess = type === 'success';

  return (
    <div
      className={`rounded-lg shadow-lg flex items-start gap-3 pl-4 pr-3 py-3.5 w-[calc(100vw-32px)] sm:min-w-[320px] sm:w-auto border ${
        isSuccess
          ? 'bg-green-100 border-green-200'
          : 'bg-red-100 border-red-200'
      } ${className}`}
    >
      {isSuccess ? (
        <CheckCircle className='text-green-600 w-5 h-5 mt-0.5 shrink-0' />
      ) : (
        <AlertCircle className='text-red-600 w-5 h-5 mt-0.5 shrink-0' />
      )}

      <p
        className={`text-sm flex-1 leading-5 font-medium ${
          isSuccess ? 'text-green-600' : 'text-red-600'
        }`}
      >
        {message}
      </p>

      <button
        onClick={onClose}
        type='button'
        className='shrink-0 -m-2 p-2'
        aria-label='Close'
      >
        <X
          className={`w-4 h-4 opacity-70 ${
            isSuccess ? 'text-green-600' : 'text-red-600'
          }`}
        />
      </button>
    </div>
  );
};

export default CustomToast;
